import React, { useState, useEffect } from "react";
import {Wallet, Receipt, Calculator, TrendingUp, FileText, CalendarDays, FileBarChart, UserMinus, HandCoins, Coins, Settings } from "lucide-react";
import RunPayroll from "../../components/admin/tabs/Payroll/RunPayroll";
import MonthlyInputs from "../../components/admin/tabs/Payroll/MonthlyInputs";
import LoansAndPayments from "../../components/admin/tabs/Payroll/LoansAndPayments";
import RequestLoan from "../../components/admin/tabs/Payroll/RequestLoan";
import CompensationInfo from "../../components/admin/tabs/Payroll/CompensationInfo";
import SettingsPayroll from "../../components/admin/tabs/Payroll/SettingsPayroll";
import MonthlyPayrollDetails from "../../components/admin/tabs/Payroll/monthlyPayroll";
import { getEmployeeData } from "../../utils/localStorage";
import { getRequest } from "../../service";

const Payroll = () => {
  const userData = getEmployeeData();
  const [activeTab, setActiveTab] = useState("Run Payroll");
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [stats, setStats] = useState({
    totalSalaryPaid: 0,
    totalEmployeeTax: 0,
    totalDeductions: 0,
    totalEmployees: 0
  });
  const [loading, setLoading] = useState(true);

  // Fetch Payroll stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const companyId = userData?.userId;
        if (!companyId) return;
        setLoading(true);
        const currentYear = new Date().getFullYear();
        const response = await getRequest(`/api/monthlysalery/stats?companyId=${companyId}`);
        if (response) {
          setStats({
            totalSalaryPaid: response.totalSalaryPaid || 0,
            totalEmployeeTax: response.totalEmployeeTax || 0,
            totalDeductions: response.totalDeductions || 0,
            totalEmployees: response.summary?.totalEmployees || 0
          });
        }
      } catch (error) {
        console.error("Error fetching payroll stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.userId]);

  // Tab definitions
  const allTabs = [
    { id: "Run Payroll", icon: <FileText size={20} />, component: <RunPayroll onRowClick={handlePayrollRowClick} /> },
    // { id: "Monthly Inputs", icon: <CalendarDays size={20} />, component: <MonthlyInputs /> },
    { id: "Loan and Payment", icon: <Wallet size={20} />, component: <LoansAndPayments /> },
    { id: "Request Loan", icon: <HandCoins size={20} />, component: <RequestLoan /> },
    { id: "Compensation Info", icon: <Coins size={20} />, component: <CompensationInfo /> },
    // { id: "Settings", icon: <Settings size={20} />, component: <SettingsPayroll /> }
  ];

  // Map tab id to Payroll permission key (exact match)
  const payrollTabPermissionMap = {
    "Run Payroll": "Run Payroll",
    "Monthly Inputs": "Monthly Inputs", 
    "Loan and Payment": "Loan and Payment",
    "Request Loan": "Request Loan",
    "Compensation Info": "Compensation Info",
    "Settings": "Settings"
  };

  // Filter tabs based on role and permissions
  let filteredTabs = [];
  const role = userData?.role;
  const payrollPerms = userData?.designation?.permissions?.Payroll || {};

  if (role === "companyadmin") {
    // Show all tabs for companyadmin
    filteredTabs = allTabs;
  } else {
    // Filter based on permissions
    filteredTabs = allTabs.filter(tab => {
      const permKey = payrollTabPermissionMap[tab.id];
      if (!permKey) return true; // If not mapped, show
      const permArr = payrollPerms[permKey];
      // Show if permission array is not empty or contains 'view'
      return Array.isArray(permArr) && (permArr.includes("view") || permArr.length > 0);
    });
  }

  // If the activeTab is not in filteredTabs, reset to first available
  React.useEffect(() => {
    if (!filteredTabs.some(tab => tab.id === activeTab)) {
      setActiveTab(filteredTabs[0]?.id || "");
    }
    // eslint-disable-next-line
  }, [filteredTabs.map(tab => tab.id).join(",")]);

  function handlePayrollRowClick(payrollData) {
    console.log('PayrollData being set:', payrollData);
    console.log('CreatedAt in payrollData:', payrollData.createdAt);
    setSelectedPayroll(payrollData);
  }

  const handleBackToPayroll = () => {
    setSelectedPayroll(null);
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    // Reset selected payroll when switching tabs
    setSelectedPayroll(null);
  };

  const activeComponent = filteredTabs.find(tab => tab.id === activeTab)?.component;
  const currency = userData?.currency || "$";
  const tiles = [
    {
      title: "Salary Paid",
      value: `${currency} ${(stats.totalSalaryPaid || 0).toLocaleString()}`,
      icon: <Wallet className="h-6 w-6 text-green-500" />,
      color: "bg-green-50"
    },
    {
      title: "Employee Tax",
      value: `${currency} ${(stats.totalEmployeeTax || 0).toLocaleString()}`,
      icon: <Coins className="h-6 w-6 text-blue-500" />,
      color: "bg-blue-50"
    },
    {
      title: "Deductions",
      value: `${currency} ${(stats.totalDeductions || 0).toLocaleString()}`,
      icon: <Calculator className="h-6 w-6 text-orange-500" />,
      color: "bg-orange-50"
    },
    {
      title: "Total Employees",
      value: String(stats.totalEmployees || 0),
      icon: <TrendingUp className="h-6 w-6 text-purple-500" />,
      color: "bg-purple-50"
    }
  ];

  // If a payroll is selected, show the details view
  if (selectedPayroll) {
    return (
      <div className="flex bg-white">
        <div className="w-64 border-r border-gray-200 bg-gray-50 fixed h-screen">
          <div className="p-4">
            <div className="flex flex-col gap-2">
              {filteredTabs.map((tab) => (
                <div key={tab.id}>
                  <button
                    className={`text-left px-4 py-2 rounded-md !capitalize transition-colors duration-300 w-full flex items-center gap-2  cursor-pointer  ${tab.id === activeTab
                        ? "bg-primary text-white"
                        : "hover:bg-gray-100 hover:text-primary"
                      }`}
                    onClick={() => handleTabClick(tab.id)}
                  >
                    {tab.icon}
                    {tab.id}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex-1 p-6 overflow-auto ml-64">
          <MonthlyPayrollDetails 
            selectedPayroll={selectedPayroll} 
            onBack={handleBackToPayroll}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex bg-white">
      <div className="w-64 border-r border-gray-200 bg-gray-50 fixed h-screen">
        <div className="p-4">
          <div className="flex flex-col gap-2">
            {filteredTabs.map((tab) => (
              <div key={tab.id}>
                <button
                  className={`text-left px-4 py-2 rounded-md !capitalize transition-colors duration-300 w-full flex items-center gap-2  cursor-pointer  ${tab.id === activeTab
                      ? "bg-primary text-white"
                      : "hover:bg-gray-100 hover:text-primary"
                    }`}
                  onClick={() => handleTabClick(tab.id)}
                >
                  {tab.icon}
                  {tab.id}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 p-6 overflow-auto ml-64">
        {/* Only show tiles when not on Loan and Payment tab */}
        {activeTab !== "Loan and Payment" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {loading ? (
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="bg-gray-100 p-4 rounded-lg shadow-sm animate-pulse">
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded"></div>
                </div>
              ))
            ) : (
              tiles.map((stat, index) => (
                <div key={index} className={`${stat.color} p-3 md:p-4 rounded-lg shadow-sm`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-gray-600 text-sm">{stat.title}</p>
                      <h3 className="text-lg md:text-xl font-bold mt-1">{stat.value}</h3>
                    </div>
                    {stat.icon}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        <div className={`bg-white rounded-lg shadow-sm ${activeTab !== "Loan and Payment" ? "mt-5" : ""}`}>
          {activeComponent}
        </div>
      </div>
    </div>
  );
};

export default Payroll;
